/**
 * 見積依頼書の紙（PDF）を画面の中で見る・印刷する・保存する（#149。見積書の紙の画面＝R13・工程表の紙の画面と同じ形）
 *   /rfqs/:id/paper … 下書きは透かし入り（API がその場で描く。保存しない）、送ったあとは保存した PDF
 *   入口は見積依頼書の画面の「PDF で確認（透かし入り）」（送るカード・右の枠の下・右の紙を押す）。書きかけを保存してから移ってくる
 *   PDF は pdf.js で描いて紙として並べ、上に「PDF 保存」「印刷」。戻るのは題の左上の「‹ 見積依頼書」（#98 の戻るボタン）
 *   印刷はこの画面の紙だけ（body.paper-mode）。売価はどこにも出ない（紙に無い）
 *   送付状（#223、docs/89 §5.1）：送った依頼だけ・PC だけ、紙の右に「送付状」の枠。オンなら「印刷」「PDF 保存」は送付状＋見積依頼書の PDF 1本（送付の記録）
 *   2回目（docs/89 §6）：枠の「同じ送付先の書類を追加」で束ねの画面へ（帳票の紙の画面と同じ＝Paper.tsx の useBundleTo）
 *   3回目の前の直し（2026-10-21 射場さんの決め＝docs/89 ★16・★17）：下書きの「PDF で確認（透かし入り）」は送付状がオン（?cover=fax|window）なら送付状込み
 *   （送付状の見本も透かし「下書き」・見るだけ＝枠は出さない）。題は「見積依頼書」と札「下書き」（題の後ろに「（下書き）」を書かない）
 */
import { COVER_LETTER_MSG, MSG, RFQ_MSG, RFQ_STATUS_LABEL, type Rfq } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useParams, useSearchParams } from "react-router";
import { api, fetchFile, messageOf } from "../../api.js";
import { BackButton, useBackNav, usePageName } from "../../components/BackButton.jsx";
import { docShort } from "../../components/AreaIcon.jsx";
import { CoverBundlePicker } from "../../components/CoverBundle.jsx";
import { CoverLetterPanel, useCoverLetter } from "../../components/CoverLetterPanel.jsx";
import { useBundleTo } from "../Paper.jsx";
import { ErrorText, useToast } from "../../components/ui.jsx";
import { downloadBlob, renderPdf, type PageImg } from "../../lib/pdfPages.js";
import { useNarrow } from "../../lib/useNarrow.js";
import { currentView } from "../../lib/view.js";

export function RfqPaperPage() {
  const { id = "" } = useParams();
  const q = useQuery({ queryKey: ["rfq", id], queryFn: () => api<Rfq>("GET", `/rfqs/${id}`), retry: false });
  const r = q.data;
  const short = r ? docShort(RFQ_MSG.title, r.no) : null;
  const back = useBackNav({ to: `/rfqs/${id}`, label: short ?? RFQ_MSG.title });
  usePageName(RFQ_MSG.title, short);
  const draft = r?.status === "draft";
  const [sp] = useSearchParams();
  /** 下書きの送付状（見積依頼書の画面の送信設定。?cover=fax|window）。見るだけ・透かし「下書き」 */
  const draftCover = draft && (sp.get("cover") === "fax" || sp.get("cover") === "window") ? (sp.get("cover") as "fax" | "window") : null;
  const label = RFQ_MSG.title;
  const subject = r ? `${r.no ? `${r.no}　` : ""}${r.vendor.shortName}　${r.subject}` : "";
  const fileName = r ? `${RFQ_MSG.title}_${r.no ?? RFQ_STATUS_LABEL.draft}_${r.vendor.shortName}.pdf`.replace(/[\\/:*?"<>|\s]+/g, "_") : `${RFQ_MSG.title}.pdf`;
  // 下書きは保存のたびに中身が変わるので、読み直した版（updatedAt）を URL に付けて描き直す
  const pdfUrl = r ? `/api/v1/rfqs/${id}/pdf?v=${encodeURIComponent(r.updatedAt)}` : null;
  const { toast, node: toastNode } = useToast();
  // 送付状（#223）：送った依頼・PC だけ
  const narrow = useNarrow();
  const coverTarget = r && (r.status === "sent" || r.status === "answered") && !narrow && currentView() !== "phone" ? { items: [{ kind: "rfq" as const, id }] } : null;
  const cover = useCoverLetter(coverTarget);
  const coverOn = !!coverTarget && cover.on;
  const draftCov = useCoverLetter(draftCover && !narrow && currentView() !== "phone" ? { items: [{ kind: "rfq" as const, id }] } : null, { switchable: false, draftRfq: true, defaultWindow: draftCover === "window", watermark: "draft" });
  const bundleTo = useBundleTo(cover);
  const qc = useQueryClient();
  const [coverBusy, setCoverBusy] = useState(false);
  const [printPages, setPrintPages] = useState<PageImg[] | null>(null);
  useEffect(() => {
    if (!printPages) return;
    const f = requestAnimationFrame(() => requestAnimationFrame(() => window.print()));
    return () => cancelAnimationFrame(f);
  }, [printPages]);
  const deliverCover = async (method: "print" | "download") => {
    setCoverBusy(true);
    try {
      const { result, blob } = await cover.deliver(method);
      void qc.invalidateQueries({ queryKey: ["deliveries"] });
      if (method === "download") downloadBlob(blob, result.fileName);
      else {
        const got: PageImg[] = [];
        await renderPdf(await blob.arrayBuffer(), (p, i) => {
          got[i - 1] = p;
        });
        setPrintPages(got);
      }
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    } finally {
      setCoverBusy(false);
    }
  };

  const [pages, setPages] = useState<PageImg[]>([]);
  const [total, setTotal] = useState(0);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    document.body.classList.add("paper-mode");
    return () => document.body.classList.remove("paper-mode");
  }, []);
  // タブの名前：「見積依頼書（下書き）：沼田電気　照明工事」
  useEffect(() => {
    if (!r) return;
    const prev = document.title;
    document.title = `${draft ? `${label}（${RFQ_STATUS_LABEL.draft}）` : label}：${subject}`;
    return () => {
      document.title = prev;
    };
  }, [r, label, subject]);

  useEffect(() => {
    if (!pdfUrl) return;
    let alive = true;
    let made: string | null = null;
    setPages([]);
    setTotal(0);
    setError(undefined);
    setLoading(true);
    (async () => {
      try {
        // 断られたら API の文（混み合い＝503 PDF_BUSY・ログイン切れ）。PDF はサーバーで順番を待つことがある（docs/72 §10）
        const blob = await fetchFile(pdfUrl);
        if (!alive) return;
        made = URL.createObjectURL(blob);
        setBlobUrl(made);
        await renderPdf(await blob.arrayBuffer(), (p, i, n) => {
          if (!alive) return;
          setTotal(n);
          setPages((ps) => {
            const next = ps.slice();
            next[i - 1] = p;
            return next;
          });
        });
      } catch (e) {
        if (alive) setError(messageOf(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [pdfUrl]);

  const doPrint = () => {
    if (!pages.length) return;
    if (coverOn) {
      void deliverCover("print");
      return;
    }
    setPrintPages(null);
    window.print();
  };

  if (q.isError) {
    return (
      <div className="soonbox">
        <ErrorText>{messageOf(q.error)}</ErrorText>
        <p>
          <Link className="btn text" to="/rfqs">{RFQ_MSG.listTitle}一覧へ</Link>
        </p>
      </div>
    );
  }

  return (
    <div className="paperview" data-testid="rfq-paper-page">
      <style>{"@page { size: A4; margin: 0; }"}</style>
      <BackButton back={back} />
      <div className="hrow paperbar">
        <h1>{label}</h1>
        {draft ? (
          <span className="chip" data-status="draft" data-testid="rfq-paper-status">
            {RFQ_STATUS_LABEL.draft}
          </span>
        ) : null}
        <span className="hint" style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {r ? subject : "読み込んでいます…"}
        </span>
        <span className="sp" />
        {coverOn ? (
          <button type="button" className="btn" disabled={!blobUrl || coverBusy || !cover.ready} onClick={() => void deliverCover("download")} data-testid="rfq-paper-save">
            PDF 保存
          </button>
        ) : (
          <a className={"btn" + (blobUrl ? "" : " disabled")} href={blobUrl ?? undefined} download={fileName} aria-disabled={!blobUrl} data-testid="rfq-paper-save">
            PDF 保存
          </a>
        )}
        <button type="button" className="btn primary" onClick={doPrint} disabled={loading || pages.length === 0 || !!error || coverBusy || (coverOn && !cover.ready)} data-testid="rfq-paper-print">
          {coverBusy ? "送付状作成中…" : "印刷"}
        </button>
      </div>
      <ErrorText>{error}</ErrorText>
      {loading && pages.length === 0 && !error ? <p className="hint" role="status" data-testid="paper-making">{MSG.pdfMaking}</p> : null}
      <div className={coverTarget ? "paperwrap" : undefined}>
        <div className="pages" aria-label={`${RFQ_MSG.title}のページ`}>
          {coverOn
            ? cover.pages.map((p, i) => (
                <div className="clsheet" key={`c${i}`} data-testid="cover-sheet">
                  <img src={p.url} alt={`送付状 ${i + 1}ページ目`} width={p.w} height={p.h} />
                </div>
              ))
            : null}
          {/* 下書きの送付状の見本（透かし「下書き」。★16） */}
          {draftCover
            ? draftCov.pages.map((p, i) => (
                <div className="clsheet" key={`d${i}`} data-testid="cover-sheet">
                  <img src={p.url} alt={`送付状 ${i + 1}ページ目`} width={p.w} height={p.h} />
                </div>
              ))
            : null}
          {pages.map((p, i) =>
            p ? (
              <div className="paperpage" key={i} data-testid="rfq-paper-sheet">
                <img src={p.url} alt={`${RFQ_MSG.title} ${i + 1}ページ目${total ? `／${total}` : ""}`} width={p.w} height={p.h} />
              </div>
            ) : null,
          )}
        </div>
        {coverTarget ? <CoverLetterPanel c={cover} onAddDocs={bundleTo.open} addDocsLabel={COVER_LETTER_MSG.addSameRecipient} /> : null}
      </div>
      {coverTarget && bundleTo.picking ? <CoverBundlePicker target={coverTarget} onClose={bundleTo.close} onPick={(refs, labels) => bundleTo.go(coverTarget.items, refs, labels)} /> : null}
      {total > 0 ? <p className="hint" style={{ textAlign: "center" }}>{`${total}ページ`}</p> : null}
      {back.dialog}
      {toastNode}
      {createPortal(
        <div id="printContainer" aria-hidden="true">
          {(coverOn && printPages ? printPages : pages).map((p, i) =>
            p ? (
              <div className="printedPage" key={i}>
                <img src={p.url} alt="" width={p.w} height={p.h} />
              </div>
            ) : null,
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
